import {
    faArrowDownToLine,
    faChevronDown,
    faExpand,
    faFileExcel,
    faFilePdf,
    faFileWord,
} from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { renderMarkdown } from './messageContent.ts';
import { parseToolOutput } from './toolDisplay.tsx';
import Button from '../../core/components/Button.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import classnames from '../../core/utils/classnames.tsx';

const DownloadIcon = withIcon(faArrowDownToLine);
const ChevronDownIcon = withIcon(faChevronDown);
const ExpandIcon = withIcon(faExpand);

const MAX_PREVIEW_ROWS = 8;

const FILE_META = {
    excel_sheet: { icon: withIcon(faFileExcel), downloadKey: 'downloadExcel' },
    word_document: { icon: withIcon(faFileWord), downloadKey: 'downloadWord' },
    pdf_document: { icon: withIcon(faFilePdf), downloadKey: 'downloadPdf' },
};

const isNumericCell = (cell) =>
    typeof cell === 'number' || (typeof cell === 'string' && /^-?\d+(\.\d+)?$/.test(cell.trim()));
const isFormulaCell = (cell) => typeof cell === 'string' && cell.startsWith('=');

const SPREADSHEET_CELL =
    'whitespace-nowrap border-0 border-b border-r border-solid border-line-weak px-2 py-1 last:border-r-0';
const SPREADSHEET_GUTTER = classnames(
    SPREADSHEET_CELL,
    'bg-surface-strong text-center text-xs font-medium text-content-medium'
);

const SpreadsheetBody = ({ output }) => {
    const { t } = useTranslation('chat');
    const rows = output.rows.slice(0, MAX_PREVIEW_ROWS);
    const hiddenRows = Math.max(0, (output.row_count ?? output.rows.length) - rows.length);
    return (
        <>
            <div className="overflow-x-auto">
                <table className="w-full border-collapse text-xs">
                    <thead>
                        <tr>
                            <th className={classnames(SPREADSHEET_GUTTER, 'w-8')}>1</th>
                            {output.columns.map((column, idx) => (
                                <th
                                    key={idx}
                                    className={classnames(
                                        SPREADSHEET_CELL,
                                        'bg-surface-medium text-left font-medium text-content-strong'
                                    )}
                                >
                                    {String(column)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="[&>tr:last-child>*]:border-b-0">
                        {rows.map((row, rowIdx) => (
                            <tr key={rowIdx}>
                                <td className={SPREADSHEET_GUTTER}>{rowIdx + 2}</td>
                                {row.map((cell, cellIdx) => (
                                    <td
                                        key={cellIdx}
                                        className={classnames(
                                            SPREADSHEET_CELL,
                                            'text-content-strong',
                                            isNumericCell(cell) && 'text-right tabular-nums',
                                            isFormulaCell(cell) && 'font-mono text-content-medium'
                                        )}
                                    >
                                        {cell === null || cell === undefined || cell === '' ? '–' : String(cell)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="flex items-center justify-between gap-3 border-0 border-t border-solid border-line-weak bg-surface-strong px-2 pb-1">
                <span className="rounded-b border border-t-0 border-solid border-line-weak bg-surface-weak px-3 py-0.5 text-xs font-medium text-content-strong">
                    {output.sheet_title}
                </span>
                {hiddenRows > 0 && (
                    <span className="text-xs text-content-medium">{t('thread.moreRows', { count: hiddenRows })}</span>
                )}
            </div>
        </>
    );
};

const DOCUMENT_STYLES = [
    'text-sm text-content-strong',
    '[&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0',
    '[&_h1]:mb-2 [&_h1]:mt-5 [&_h1]:text-base [&_h1]:font-semibold [&_h1:first-child]:mt-0',
    '[&_h2]:mb-1.5 [&_h2]:mt-4 [&_h2]:text-sm [&_h2]:font-medium [&_h2:first-child]:mt-0',
    '[&_h3]:mb-1 [&_h3]:mt-3 [&_h3]:text-sm [&_h3]:font-medium [&_h3:first-child]:mt-0',
    '[&_h4]:mb-1 [&_h4]:mt-3 [&_h4]:text-sm [&_h4]:font-medium [&_h4]:text-content-medium',
    '[&_ul]:my-2 [&_ul]:pl-5 [&_li]:my-0.5 [&_strong]:font-medium',
    '[&_table]:my-3 [&_table]:w-full [&_table]:border-collapse [&_table]:text-xs',
    '[&_th]:border [&_th]:border-solid [&_th]:border-line-weak [&_th]:bg-surface-medium [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:font-medium',
    '[&_td]:border [&_td]:border-solid [&_td]:border-line-weak [&_td]:px-2 [&_td]:py-1',
].join(' ');

const DocumentPageBody = ({ output }) => (
    <div className="max-h-96 overflow-y-auto bg-surface-medium p-4">
        <div className="mx-auto max-w-2xl rounded-lg border border-solid border-line-weak bg-surface-weak px-10 py-8">
            {output.title && (
                <h1 className="m-0 mb-4 border-0 border-b border-solid border-line-weak pb-3 text-base font-semibold text-content-strong">
                    {output.title}
                </h1>
            )}
            <div
                className={DOCUMENT_STYLES}
                dangerouslySetInnerHTML={{ __html: renderMarkdown(output.preview_markdown) }}
            />
        </div>
    </div>
);

export const GeneratedFileCard = ({ toolCall, attachments = [], onPreviewAttachment = null }) => {
    const { t } = useTranslation('chat');
    const [expanded, setExpanded] = useState(false);
    const output = parseToolOutput(toolCall.tool_output);
    const attachment = attachments.find((candidate) => candidate.id === output.attachment_id);
    const spreadsheet = output.type === 'excel_sheet';
    const { icon: FileIcon, downloadKey } = FILE_META[output.type];
    const previewable = Boolean(onPreviewAttachment && attachment?.previewUrl && output.type === 'pdf_document');
    const summary = spreadsheet
        ? t('thread.sheetSummary', { rows: output.row_count ?? output.rows.length, columns: output.columns.length })
        : t(`thread.fileKinds.${output.type}`);

    return (
        <div className="mt-2.5 w-full overflow-hidden rounded-lg border border-solid border-line-weak bg-surface-weak">
            <div
                className={classnames(
                    'flex items-center gap-3 px-3 py-2',
                    expanded && 'border-0 border-b border-solid border-line-weak'
                )}
            >
                <FileIcon className="flex-none text-content-medium" />
                <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-medium text-content-strong" title={output.file_name}>
                        {output.file_name}
                    </div>
                    <div className="truncate text-xs text-content-medium">{summary}</div>
                </div>
                <div className="flex flex-none items-center gap-1.5">
                    <Button variant="ghost" aria-expanded={expanded} onClick={() => setExpanded((value) => !value)}>
                        {t(expanded ? 'thread.hidePreview' : 'thread.showPreview')}
                        <ChevronDownIcon className={classnames('ml-1.5', expanded && 'rotate-180')} />
                    </Button>
                    {previewable && (
                        <Button variant="ghost" onClick={() => onPreviewAttachment(attachment)}>
                            <ExpandIcon className="mr-1.5" />
                            {t('thread.fullScreen')}
                        </Button>
                    )}
                    {attachment?.downloadUrl && (
                        <Button variant="outline" asChild>
                            <a href={attachment.downloadUrl} download={output.file_name}>
                                <DownloadIcon className="mr-1.5" />
                                {t(`thread.${downloadKey}`)}
                            </a>
                        </Button>
                    )}
                </div>
            </div>
            {expanded && (spreadsheet ? <SpreadsheetBody output={output} /> : <DocumentPageBody output={output} />)}
        </div>
    );
};
